
import { supabase } from "@/integrations/supabase/client";

// Type definitions for database function responses
interface CleanupSiteResult {
  success: boolean;
  message?: string;
  site_id?: string;
  site_number?: string;
  original_count?: number;
  cleaned_count?: number;
}

interface ProcessPaymentResult {
  success: boolean;
  error?: string;
  message?: string;
  booking_ids?: string[];
  total_created?: number;
  partial_success?: boolean;
  failures?: string[];
  already_exists?: boolean;
}

export const runDatabaseMigration = async (): Promise<boolean> => {
  try {
    console.log("Starting improved database migration...");
    
    // Step 1: Clean up booked_dates for problematic sites using existing function
    try {
      console.log("Cleaning up problematic sites with standard function...");
      const problematicSites = ["SC-RV-01", "SC-RV-02", "MV-RV-01", "WH-01"];
      let cleanedCount = 0;
      
      for (const siteNumber of problematicSites) {
        try {
          // First get the site ID
          const { data: site, error: siteError } = await supabase
            .from('camping_sites')
            .select('id')
            .eq('site_number', siteNumber)
            .single();
          
          if (siteError) {
            console.error(`Error finding site ${siteNumber}:`, siteError);
            continue;
          }
          
          if (site) {
            const { error: cleanupError } = await supabase
              .rpc('cleanup_site_booked_dates', { site_id: site.id });
            
            if (cleanupError) {
              console.error(`Error cleaning site ${siteNumber}:`, cleanupError);
            } else {
              console.log(`Successfully cleaned site ${siteNumber}`);
              cleanedCount++;
            }
          }
        } catch (siteError) {
          console.error(`Exception cleaning site ${siteNumber}:`, siteError);
        }
      }
      
      if (cleanedCount > 0) {
        console.log(`Successfully cleaned ${cleanedCount} problematic site(s).`);
      }
    } catch (siteCleanupError) {
      console.error("Error during site cleanup:", siteCleanupError);
    }
    
    // Step 2: Check for and handle unprocessed webhook payments manually
    try {
      console.log("Checking for unprocessed webhook payments...");
      
      // Find any payment verifications that have a successful payment but no bookings
      const { data: unprocessedPayments, error: unprocessedError } = await supabase
        .from('payment_verifications')
        .select('*')
        .eq('payment_status', 'paid')
        .is('booking_ids', null)
        .is('webhook_processed_at', null);
        
      if (unprocessedError) {
        console.error("Error checking for unprocessed payments:", unprocessedError);
      } else if (unprocessedPayments && unprocessedPayments.length > 0) {
        console.log(`Found ${unprocessedPayments.length} unprocessed payments`, unprocessedPayments);
        
        let recoveredCount = 0;
        
        // Process each unprocessed payment using the wrapper function
        for (const payment of unprocessedPayments) {
          try {
            console.log(`Attempting to process payment ${payment.session_id} using wrapper function...`);
            
            const { data: result, error: recoveryError } = await supabase
              .rpc('process_payment_and_create_booking', { payment_session_id: payment.session_id });
            
            if (recoveryError) {
              console.error(`Error processing payment ${payment.session_id}:`, recoveryError);
              continue;
            }
            
            // Cast the result to the expected type using unknown first
            const typedResult = result as unknown as ProcessPaymentResult;
            
            if (typedResult?.success) {
              console.log(`Successfully processed payment ${payment.session_id}:`, typedResult);
              recoveredCount++;
            } else {
              console.log(`Could not process payment ${payment.session_id}:`, typedResult?.error || "Unknown error");
            }
          } catch (paymentError) {
            console.error(`Error processing payment ${payment.session_id}:`, paymentError);
          }
        }
        
        // Log recovery results instead of showing toast
        if (recoveredCount > 0) {
          console.log(`Successfully recovered ${recoveredCount} booking(s) using the wrapper function.`);
        }
      } else {
        console.log("No unprocessed payments found");
      }
    } catch (webhookError) {
      console.error("Error checking webhook processing:", webhookError);
    }
    
    // Step 3: Check for site bookings without booked_dates entries using improved parameter names
    try {
      console.log("Checking for site bookings without booked_dates entries...");
      
      const { data: bookings, error: bookingsError } = await supabase
        .from('site_bookings')
        .select(`
          id,
          booking_id,
          site_id,
          start_date,
          end_date,
          payment_status
        `)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })
        .limit(50); // Only check recent bookings
        
      if (bookingsError) {
        console.error("Error fetching site bookings:", bookingsError);
      } else if (bookings && bookings.length > 0) {
        console.log(`Found ${bookings.length} recent paid bookings to verify`);
        
        let fixedCount = 0;
        
        for (const booking of bookings) {
          try {
            // Check if the site has the corresponding booking dates
            const { data: site, error: siteError } = await supabase
              .from('camping_sites')
              .select('booked_dates')
              .eq('id', booking.site_id)
              .single();
              
            if (siteError) {
              console.error(`Error fetching site ${booking.site_id}:`, siteError);
              continue;
            }
            
            // Check if the booking dates are in the site's booked_dates array
            const startDate = new Date(booking.start_date);
            const endDate = new Date(booking.end_date);
            
            let hasBookingDates = false;
            
            if (site.booked_dates && site.booked_dates.length > 0) {
              // Try to find the booking dates in the array
              for (const dateRange of site.booked_dates) {
                try {
                  // Basic string matching - not perfect but should catch most cases
                  const rangeStr = String(dateRange);
                  if (rangeStr.includes(startDate.toISOString().split('T')[0]) &&
                      rangeStr.includes(endDate.toISOString().split('T')[0])) {
                    hasBookingDates = true;
                    break;
                  }
                } catch (parseError) {
                  console.error(`Error parsing date range ${dateRange}:`, parseError);
                }
              }
            }
            
            if (!hasBookingDates) {
              console.log(`Fixing missing booked_dates for booking ${booking.booking_id}`);
              
              // Add the booking dates to the site's booked_dates array using correct parameter names
              const { error: updateError } = await supabase.rpc('update_booked_dates', {
                site_id_param: booking.site_id,
                start_date_param: booking.start_date,
                end_date_param: booking.end_date
              });
              
              if (updateError) {
                console.error(`Error updating booked_dates for booking ${booking.booking_id}:`, updateError);
              } else {
                console.log(`Successfully fixed booked_dates for booking ${booking.booking_id}`);
                fixedCount++;
              }
            }
          } catch (bookingError) {
            console.error(`Error processing booking ${booking.booking_id}:`, bookingError);
          }
        }
        
        // Log results instead of showing toast
        if (fixedCount > 0) {
          console.log(`Fixed ${fixedCount} site booking(s) with missing availability data.`);
        }
      } else {
        console.log("No paid bookings found to check");
      }
    } catch (bookingCheckError) {
      console.error("Error checking site bookings:", bookingCheckError);
    }
    
    console.log("Improved database migration completed successfully");
    return true;
  } catch (error) {
    console.error("Unexpected error during improved database migration:", error);
    // Log error instead of showing toast
    console.error("An unexpected error occurred during database migration.");
    return false;
  }
};
